import React, { useEffect, useState } from 'react'
import { X, ZoomIn, HelpCircle, ShieldCheck } from 'lucide-react'
import BeforeAfterSlider from '../components/BeforeAfterSlider'

export default function Gallery() {
  const [items, setItems] = useState([])
  const [filteredItems, setFilteredItems] = useState([])
  const [activeFilter, setActiveFilter] = useState('Alle')
  const [selectedItem, setSelectedItem] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const res = await fetch('https://api.dev.auxo.dk/public/content/gallery_items')
        if (res.ok) {
          const data = await res.json()
          setItems(data)
          setFilteredItems(data)
        }
      } catch (error) {
        console.error('Fejl ved hentning af galleri:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchGallery()
  }, [])

  // Categories list
  const categories = ['Alle', 'Indendørs', 'Udendørs', 'Erhverv', 'Detaljer']

  const handleFilterChange = (filter) => {
    setActiveFilter(filter)
    if (filter === 'Alle') {
      setFilteredItems(items)
    } else {
      setFilteredItems(items.filter(item => item.category?.da === filter))
    }
  }

  return (
    <div className="bg-slate-50 min-h-screen pb-0">
      {/* Page Header */}
      <section className="bg-white pt-16 pb-8 border-b border-slate-200">
        <div className="mx-auto max-w-4xl px-4 text-center space-y-3">
          <h1 className="text-3xl font-semibold sm:text-4xl lg:text-5xl text-slate-900">Vores Galleri</h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Gå på opdagelse i vores referencer og se eksempler på det malerarbejde, vi udfører. Vi leverer altid et resultat, vi kan være stolte af.
          </p>
        </div>
      </section>

      {/* Filter Tabs */}
      <section className="py-8 bg-white border-b border-slate-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-center gap-2">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => handleFilterChange(category)}
                className={`rounded-full px-5 py-2 text-sm font-semibold transition-all duration-200 ${
                  activeFilter === category
                    ? 'bg-black text-white shadow-md shadow-slate-200'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-slate-900'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-12">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="h-10 w-10 animate-spin rounded-full border-4 border-black border-t-transparent" />
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl border border-slate-200 p-8 max-w-md mx-auto">
              <HelpCircle className="mx-auto h-12 w-12 text-slate-400" />
              <h3 className="mt-4 text-lg font-bold text-slate-900">Ingen billeder fundet</h3>
              <p className="mt-2 text-sm text-slate-600">Der er i øjeblikket ingen projekter i denne kategori. Tjek venligst igen senere.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {filteredItems.map((item) => (
                <div
                  key={item.id}
                  onClick={() => setSelectedItem(item)}
                  className="group relative overflow-hidden rounded-2xl bg-white border border-slate-200 shadow-sm hover:shadow-md transition-all duration-300 cursor-pointer"
                >
                  <div className="aspect-4/3 w-full overflow-hidden bg-slate-100">
                    <img
                      src={item.image?.[0]}
                      alt={item.title?.da}
                      className="h-full w-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                  {/* Hover Overlay */}
                  <div className="absolute inset-0 bg-slate-950/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                    <div className="rounded-full bg-white/90 p-3 text-slate-900 shadow-md transform scale-90 group-hover:scale-100 transition-transform duration-300">
                      <ZoomIn className="h-6 w-6" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Featured Before/After Slider */}
      <section className="py-12 bg-white border-t border-b border-slate-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-left max-w-2xl mb-10 space-y-2">
            <h2 className="text-2xl font-bold text-slate-900 sm:text-3xl">
              Før og efter forvandlinger
            </h2>
            <p className="text-sm sm:text-base text-slate-600">
              Træk i skyderne for at se den markante forskel før og efter vores professionelle malerarbejde.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Projekt: Indvendigt rum med blå væg */}
            <div className="flex flex-col space-y-3 bg-white p-4 rounded-2xl border border-slate-200/60 shadow-xs">
              <div className="overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
                <BeforeAfterSlider
                  beforeImage="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/8102f19c-aab0-44cc-aa15-a120b6503274/02-interior-room-before.webp"
                  afterImage="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/8102f19c-aab0-44cc-aa15-a120b6503274/04-interior-blue-wall-after.webp"
                  beforeLabel="Før"
                  afterLabel="Efter"
                />
              </div>
              <div className="text-center pt-1">
                <h3 className="text-base font-semibold text-slate-900">Indvendigt rum med blå væg</h3>
              </div>
            </div>

            {/* Projekt: Grå indvendig væg */}
            <div className="flex flex-col space-y-3 bg-white p-4 rounded-2xl border border-slate-200/60 shadow-xs">
              <div className="overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
                <BeforeAfterSlider
                  beforeImage="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/8102f19c-aab0-44cc-aa15-a120b6503274/06-grey-wall-before-or-during.webp"
                  afterImage="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/8102f19c-aab0-44cc-aa15-a120b6503274/05-grey-wall-after.webp"
                  beforeLabel="Før"
                  afterLabel="Efter"
                />
              </div>
              <div className="text-center pt-1">
                <h3 className="text-base font-semibold text-slate-900">Grå indvendig væg</h3>
              </div>
            </div>

            {/* Projekt: Udvendig træfacade */}
            <div className="flex flex-col space-y-3 bg-white p-4 rounded-2xl border border-slate-200/60 shadow-xs">
              <div className="overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
                <BeforeAfterSlider
                  beforeImage="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/8102f19c-aab0-44cc-aa15-a120b6503274/08-exterior-wood-facade-before-or-during.webp"
                  afterImage="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/8102f19c-aab0-44cc-aa15-a120b6503274/07-exterior-wood-facade-after.webp"
                  beforeLabel="Før"
                  afterLabel="Efter"
                />
              </div>
              <div className="text-center pt-1">
                <h3 className="text-base font-semibold text-slate-900">Udvendig træfacade</h3>
              </div>
            </div>

            {/* Projekt: Hvidt skab/møbel */}
            <div className="flex flex-col space-y-3 bg-white p-4 rounded-2xl border border-slate-200/60 shadow-xs">
              <div className="overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
                <BeforeAfterSlider
                  beforeImage="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/8102f19c-aab0-44cc-aa15-a120b6503274/10-cabinet-before.webp"
                  afterImage="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/8102f19c-aab0-44cc-aa15-a120b6503274/09-cabinet-after.webp"
                  beforeLabel="Før"
                  afterLabel="Efter"
                />
              </div>
              <div className="text-center pt-1">
                <h3 className="text-base font-semibold text-slate-900">Hvidt skab/møbel</h3>
              </div>
            </div>

            {/* Project 1 */}
            <div className="flex flex-col space-y-3 bg-white p-4 rounded-2xl border border-slate-200/60 shadow-xs">
              <div className="overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
                <BeforeAfterSlider
                  beforeImage="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/8102f19c-aab0-44cc-aa15-a120b6503274/facade-og-terasse-fa-r.jpeg"
                  afterImage="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/8102f19c-aab0-44cc-aa15-a120b6503274/facade-og-terasse-efter.jpeg"
                  beforeLabel="Før"
                  afterLabel="Efter"
                />
              </div>
              <div className="text-center pt-1">
                <h3 className="text-base font-semibold text-slate-900">Facade & Træterrasse</h3>
              </div>
            </div>

            {/* Project 2 */}
            <div className="flex flex-col space-y-3 bg-white p-4 rounded-2xl border border-slate-200/60 shadow-xs">
              <div className="overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
                <BeforeAfterSlider
                  beforeImage="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/8102f19c-aab0-44cc-aa15-a120b6503274/indenda-rs-malerarbejde-fa-r.jpg"
                  afterImage="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/8102f19c-aab0-44cc-aa15-a120b6503274/download.jpg"
                  beforeLabel="Før"
                  afterLabel="Efter"
                />
              </div>
              <div className="text-center pt-1">
                <h3 className="text-base font-semibold text-slate-900">Indendørs malerarbejde</h3>
              </div>
            </div>

            {/* Project 3 */}
            <div className="flex flex-col space-y-3 bg-white p-4 rounded-2xl border border-slate-200/60 shadow-xs">
              <div className="overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
                <BeforeAfterSlider
                  beforeImage="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/8102f19c-aab0-44cc-aa15-a120b6503274/img-2086_1.png"
                  afterImage="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/8102f19c-aab0-44cc-aa15-a120b6503274/img-2087_1.png"
                  beforeLabel="Før"
                  afterLabel="Efter"
                />
              </div>
              <div className="text-center pt-1">
                <h3 className="text-base font-semibold text-slate-900">Træværk & Vinduer</h3>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {selectedItem && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/95 p-4 backdrop-blur-sm animate-fade-in cursor-zoom-out"
          onClick={() => setSelectedItem(null)}
        >
          <button
            onClick={() => setSelectedItem(null)}
            className="absolute top-6 right-6 rounded-full bg-white/10 p-3 text-white hover:bg-white/20 transition-colors z-50 cursor-pointer"
          >
            <X className="h-6 w-6" />
          </button>

          <div
            className="relative max-w-5xl w-full max-h-[85vh] flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={selectedItem.image?.[0]}
              alt="Galleri reference"
              className="max-w-full max-h-[85vh] rounded-2xl object-contain shadow-2xl border border-slate-800"
            />
          </div>
        </div>
      )}
    </div>
  )
}
